"use client"

import { useState, useEffect } from "react"
import styled from "styled-components"
import { MessageSquare, Send, Heart, MoreHorizontal, Reply, Edit, Trash2, User } from "lucide-react"
import { useAuth } from "../context/AuthContext"
import { useTheme } from "../context/ThemeContext"

const CommentContainer = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-top: 1px solid ${props => props.theme.border};
  padding: 16px;
`

const CommentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`

const CommentTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`

const CommentForm = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  padding: 12px;
  background-color: ${props => props.theme.mode === 'dark' ? '#2d3748' : '#f9f9f9'};
  border-radius: 8px;
`

const CommentAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => props.theme.mode === 'dark' ? '#4a5568' : '#eee'};
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.textSecondary};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const CommentInputWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const CommentTextarea = styled.textarea`
  width: 100%;
  min-height: 60px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  resize: vertical;
  outline: none;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  
  &:focus {
    border-color: #2563eb;
  }
  
  &::placeholder {
    color: ${props => props.theme.textSecondary};
  }
`

const CommentActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`

const CommentButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  
  &.primary {
    background-color: #2563eb;
    color: white;
    
    &:hover {
      background-color: #1d4ed8;
    }
  }
  
  &.secondary {
    background-color: ${props => props.theme.mode === 'dark' ? '#4a5568' : '#f3f4f6'};
    color: ${props => props.theme.textSecondary};
    
    &:hover {
      background-color: ${props => props.theme.mode === 'dark' ? '#2d3748' : '#e5e7eb'};
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const CommentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const CommentItem = styled.div`
  display: flex;
  gap: 12px;
  
  &.reply {
    margin-left: 52px;
    padding-top: 12px;
    border-top: 1px solid ${props => props.theme.border};
  }
`

const CommentContent = styled.div`
  flex: 1;
`

const CommentMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`

const CommentAuthor = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: ${props => props.theme.textPrimary};
`

const CommentTime = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
`

const CommentDropdown = styled.div`
  position: relative;
`

const CommentMoreButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.textSecondary};
  padding: 4px;
  border-radius: 4px;
  
  &:hover {
    background-color: ${props => props.theme.mode === 'dark' ? '#4a5568' : '#f5f5f5'};
  }
`

const CommentDropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, ${props => props.theme.mode === 'dark' ? '0.3' : '0.1'});
  z-index: 1000;
  min-width: 100px;
  overflow: hidden;
`

const CommentDropdownItem = styled.button`
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${props => props.theme.textPrimary};

  &:hover {
    background-color: ${props => props.theme.mode === 'dark' ? '#4a5568' : '#f5f5f5'};
  }

  &.danger {
    color: #ef4444;
    
    &:hover {
      background-color: ${props => props.theme.mode === 'dark' ? '#553c3c' : '#fee2e2'};
    }
  }
`

const CommentText = styled.div`
  font-size: 14px;
  color: ${props => props.theme.textPrimary};
  line-height: 1.5;
  margin-bottom: 8px;
  white-space: pre-line;
`

const CommentFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const CommentAction = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  
  &:hover {
    background-color: ${props => props.theme.mode === 'dark' ? '#4a5568' : '#f5f5f5'};
  }
  
  &.active {
    color: #2563eb;
  }
`

const EmptyMessage = styled.div`
  text-align: center;
  color: ${props => props.theme.textSecondary};
  font-size: 14px;
  padding: 20px;
`

// 날짜 포맷 함수
const formatCommentDate = (dateString) => {
  if (!dateString) return "";
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  
  if (diffSec < 60) return "방금 전";
  if (diffMin < 60) return `${diffMin}분 전`;
  if (diffHour < 24) return `${diffHour}시간 전`;
  if (diffDay < 7) return `${diffDay}일 전`;
  
  return `${date.getFullYear()}.${date.getMonth()+1}.${date.getDate()}`;
};

export default function CommentSection({ postId, initialCommentCount = 0 }) {
  const { isLoggedIn, user } = useAuth()
  const { theme } = useTheme()
  const [comments, setComments] = useState([])
  const [loading, setLoading] = useState(false)
  const [commentText, setCommentText] = useState("")
  const [replyText, setReplyText] = useState("")
  const [replyingTo, setReplyingTo] = useState(null)
  const [editingComment, setEditingComment] = useState(null)
  const [editText, setEditText] = useState("")
  const [dropdownOpen, setDropdownOpen] = useState(null)
  const [commentCount, setCommentCount] = useState(initialCommentCount)
  const [showComments, setShowComments] = useState(true)

  // 댓글 목록 로드
  const fetchComments = async () => {
    if (!postId) return;
    
    setLoading(true)
    try {
      console.log('댓글 목록 로드 시작:', postId);
      const response = await fetch(`/comment/list/${postId}`, {
        credentials: 'include'
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json()
      console.log('댓글 목록 응답:', data);
      
      if (data.success) {
        setComments(data.comments)
        setCommentCount(data.comments.reduce((count, comment) => count + 1 + comment.replies.length, 0))
      } else {
        console.error('댓글 로드 실패:', data.message);
      }
    } catch (error) {
      console.error('댓글 로드 오류:', error)
    } finally {
      setLoading(false)
    }
  }

  // 컴포넌트 마운트 시 댓글 수만 로드
  useEffect(() => {
    const fetchCommentCount = async () => {
      if (!postId) return;
      
      try {
        const response = await fetch(`/comment/list/${postId}`, {
          credentials: 'include'
        })
        
        if (!response.ok) return;
        
        const data = await response.json()
        if (data.success) {
          setCommentCount(data.comments.reduce((count, comment) => count + 1 + comment.replies.length, 0))
        }
      } catch (error) {
        console.error('댓글 수 로드 오류:', error)
      }
    }

    if (!showComments) {
      fetchCommentCount()
    } else {
      fetchComments()
    }
  }, [postId, showComments])

  // 댓글 작성
  const handleSubmitComment = async () => {
    if (!isLoggedIn || !commentText.trim()) return;

    setLoading(true)
    try {
      console.log('댓글 작성 시작:', { postId, content: commentText.trim() });
      const response = await fetch('/comment/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          postId,
          content: commentText.trim(),
          parentId: null
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json()
      console.log('댓글 작성 응답:', data);
      
      if (data.success) {
        setCommentText("")
        await fetchComments()
      } else {
        alert(data.message)
      }
    } catch (error) {
      console.error('댓글 작성 오류:', error)
      alert('댓글 작성 중 오류가 발생했습니다.')
    } finally {
      setLoading(false)
    }
  }

  // 대댓글 작성
  const handleSubmitReply = async (parentId) => {
    if (!isLoggedIn || !replyText.trim()) return;

    setLoading(true)
    try {
      const response = await fetch('/comment/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          postId,
          content: replyText.trim(),
          parentId
        })
      })

      const data = await response.json()
      if (data.success) {
        setReplyText("")
        setReplyingTo(null)
        await fetchComments()
      } else {
        alert(data.message)
      }
    } catch (error) {
      console.error('대댓글 작성 오류:', error)
      alert('대댓글 작성 중 오류가 발생했습니다.')
    } finally {
      setLoading(false)
    }
  }

  // 댓글 수정
  const handleEditComment = async (commentId) => {
    if (!editText.trim()) return;

    setLoading(true)
    try {
      const response = await fetch(`/comment/update/${commentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          content: editText.trim()
        })
      })

      const data = await response.json()
      if (data.success) {
        setEditingComment(null)
        setEditText("")
        await fetchComments()
      } else {
        alert(data.message)
      }
    } catch (error) {
      console.error('댓글 수정 오류:', error)
      alert('댓글 수정 중 오류가 발생했습니다.')
    } finally {
      setLoading(false)
    }
  }

  // 댓글 삭제
  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('댓글을 삭제하시겠습니까?')) return;

    setLoading(true)
    try {
      const response = await fetch(`/comment/delete/${commentId}`, {
        method: 'DELETE',
        credentials: 'include'
      })

      const data = await response.json()
      if (data.success) {
        await fetchComments()
      } else {
        alert(data.message)
      }
    } catch (error) {
      console.error('댓글 삭제 오류:', error)
      alert('댓글 삭제 중 오류가 발생했습니다.')
    } finally {
      setLoading(false)
      setDropdownOpen(null)
    }
  }

  // 댓글 렌더링
  const renderComment = (comment, isReply = false) => (
    <CommentItem key={comment.id} className={isReply ? 'reply' : ''} theme={theme}>
      <CommentAvatar theme={theme}>
        {comment.avatar_url ? (
          <img src={comment.avatar_url} alt={comment.username} />
        ) : (
          <User size={24} />
        )}
      </CommentAvatar>
      <CommentContent>
        <CommentMeta>
          <CommentAuthor theme={theme}>{comment.username}</CommentAuthor>
          <CommentTime theme={theme}>{formatCommentDate(comment.created_at)}</CommentTime>
          {isLoggedIn && user && user.username === comment.username && (
            <CommentDropdown>
              <CommentMoreButton theme={theme} onClick={() => setDropdownOpen(dropdownOpen === comment.id ? null : comment.id)}>
                <MoreHorizontal size={16} />
              </CommentMoreButton>
              {dropdownOpen === comment.id && (
                <CommentDropdownMenu theme={theme}>
                  <CommentDropdownItem theme={theme} onClick={() => {
                    setEditingComment(comment.id)
                    setEditText(comment.content)
                    setDropdownOpen(null)
                  }}>
                    <Edit size={12} />
                    수정
                  </CommentDropdownItem>
                  <CommentDropdownItem 
                    theme={theme}
                    className="danger" 
                    onClick={() => handleDeleteComment(comment.id)}
                  >
                    <Trash2 size={12} />
                    삭제
                  </CommentDropdownItem>
                </CommentDropdownMenu>
              )}
            </CommentDropdown>
          )}
        </CommentMeta>
        
        {editingComment === comment.id ? (
          <CommentInputWrapper>
            <CommentTextarea
              theme={theme}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              placeholder="댓글을 수정하세요..."
            />
            <CommentActions>
              <CommentButton 
                theme={theme}
                className="secondary"
                onClick={() => {
                  setEditingComment(null)
                  setEditText("")
                }}
              >
                취소
              </CommentButton>
              <CommentButton 
                theme={theme}
                className="primary"
                onClick={() => handleEditComment(comment.id)}
                disabled={loading || !editText.trim()}
              >
                수정
              </CommentButton>
            </CommentActions>
          </CommentInputWrapper>
        ) : (
          <>
            <CommentText theme={theme}>{comment.content}</CommentText>
            <CommentFooter>
              {!isReply && isLoggedIn && (
                <CommentAction theme={theme} onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}>
                  <Reply size={14} />
                  답글
                </CommentAction>
              )}
            </CommentFooter>
          </>
        )}
        
        {/* 대댓글 작성 폼 */}
        {replyingTo === comment.id && isLoggedIn && (
          <CommentForm theme={theme} style={{ marginTop: '12px' }}>
            <CommentAvatar theme={theme}>
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt={user.username} />
              ) : (
                <User size={24} />
              )}
            </CommentAvatar>
            <CommentInputWrapper>
              <CommentTextarea
                theme={theme}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="답글을 작성하세요..."
              />
              <CommentActions>
                <CommentButton 
                  theme={theme}
                  className="secondary"
                  onClick={() => {
                    setReplyingTo(null)
                    setReplyText("")
                  }}
                >
                  취소
                </CommentButton>
                <CommentButton 
                  theme={theme}
                  className="primary"
                  onClick={() => handleSubmitReply(comment.id)}
                  disabled={loading || !replyText.trim()}
                >
                  <Send size={14} />
                  답글 작성
                </CommentButton>
              </CommentActions>
            </CommentInputWrapper>
          </CommentForm>
        )}
      </CommentContent>
    </CommentItem>
  )

  return (
    <CommentContainer theme={theme}>
      <CommentHeader>
        <CommentTitle theme={theme} onClick={() => setShowComments(!showComments)} style={{ cursor: 'pointer' }}>
          <MessageSquare size={18} />
          댓글 {commentCount}개
        </CommentTitle>
      </CommentHeader>

      {showComments && (
        <>
          {/* 댓글 작성 폼 */}
          {isLoggedIn ? (
            <CommentForm theme={theme}>
              <CommentAvatar theme={theme}>
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt={user.username} />
                ) : (
                  <User size={24} />
                )}
              </CommentAvatar>
              <CommentInputWrapper>
                <CommentTextarea
                  theme={theme}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="댓글을 작성하세요..."
                />
                <CommentActions>
                  <CommentButton 
                    theme={theme}
                    className="primary"
                    onClick={handleSubmitComment}
                    disabled={loading || !commentText.trim()}
                  >
                    <Send size={14} />
                    댓글 작성
                  </CommentButton>
                </CommentActions>
              </CommentInputWrapper>
            </CommentForm>
          ) : (
            <EmptyMessage theme={theme}>댓글을 작성하려면 로그인이 필요합니다.</EmptyMessage>
          )}

          {/* 댓글 목록 */}
          <CommentList>
            {loading && comments.length === 0 ? (
              <EmptyMessage theme={theme}>댓글을 불러오는 중...</EmptyMessage>
            ) : comments.length === 0 ? (
              <EmptyMessage theme={theme}>첫 번째 댓글을 남겨보세요!</EmptyMessage>
            ) : (
              comments.map(comment => (
                <div key={comment.id}>
                  {renderComment(comment)}
                  {comment.replies && comment.replies.map(reply => renderComment(reply, true))}
                </div>
              ))
            )}
          </CommentList>
        </>
      )}
    </CommentContainer>
  )
}